import { ingestEnemyDefs } from "./enemy-defs.js";
/**
 * The shared client viewport — the one place the game and the arena both
 * render from, so they can't drift apart. Owns the canvas → Renderer (iso,
 * painter sort, the smooth follow camera, hover outline, click markers), the
 * cosmetic interpolation (interpolate.ts — the shared movement), the hover +
 * left-click + right-click input model (the RuneScape interaction), and the
 * rAF frame loop. The host plugs in what differs via ViewportHooks:
 *   - primaryAction / onPrimaryAction: what a left click means + how it's sent
 *     (game: walk/interact/enter · arena: walk/attack).
 *   - contextItems: the right-click menu for a tile.
 *   - overlay: a host-only canvas pass (the arena's HP bars/splats/banner).
 *   - onKey: host keyboard (the arena's Space/WASD).
 *
 * Pure rendering — every position comes from the latest authoritative
 * observation; the client interpolates + decides intentions, never outcomes
 * (AGENTS §6). The wall-clock (rAF `now`) is used only for cosmetics.
 */
import type { Observation, ObservedPlayer } from "@pd/shared";
import { TICK_MS } from "@pd/shared";
import { Renderer } from "./render.js";
import type { PlayerLook, RenderState, WorldProp } from "./render.js";
import { ContextMenu } from "./context.js";
import type { CtxItem, CtxMenu } from "./context.js";
import { screenToTile as isoScreenToTile } from "./iso.js";
import { CURSOR_MARK_DURATION, MARKER_CONFIG, cursorMarkForAction, drawCursorMark, markerExpired, markerForAction, pushMarker } from "./markers.js";
import type { CursorMark, Marker } from "./markers.js";
import { applyPounceVisuals } from "./pounce.js";
import { drawChatBubbles } from "./chatBubbles.js";
import type { ChatBubbleStore } from "./chatBubbles.js";
import { ingestEnemySpeech } from "./enemy-speech.js";
import { enemyByKind } from "./enemy-defs.js";
import { createInterpState, stepInterpolation, applyCombatAnims, playerKey, enemyKey } from "./interpolate.js";
import type { InterpState, MovingEntity } from "./interpolate.js";
import { createEffectState, stepEffects, drawEffects } from "./effects.js";
import type { EffectState } from "./effects.js";
import { enemyAtScreenPoint } from "./hitbox.js";
import type { PrimaryAction, Vec2, WorldObject } from "./actions.js";
import type { PlayerAnim, HeroAssets } from "./hero.js";


/** The canvas state a host can draw with after the shared render pass (screen
 *  space, CSS pixels — use tileToScreen(x,y,isoUnit,camX,camY) to place
 *  world-anchored chrome). */
export interface OverlayApi {
  ctx: CanvasRenderingContext2D;
  obs: Observation;
  meId: number;
  positions: Map<string, Vec2>;
  anims: Map<string, PlayerAnim>;
  camX: number;
  camY: number;
  isoUnit: number;
  cssW: number;
  cssH: number;
  now: number;
}

/** Optional dev/introspection target (the game's `window.__game` CDP probe).
 * The Viewport writes the per-frame render state into it; the host writes the
 * rest (obs/info/tick). Read-only view; never a shipping data path. */
export interface ViewportDebug {
  positions: Map<string, Vec2>;
  anims: Map<string, PlayerAnim>;
  markers: Marker[];
  cursorMark?: CursorMark | null;
  /** The hover-resolved tile (enemy-body snapped, like the click), or null
   *  while the cursor is off the canvas — the arena's aim reticle reads it
   *  (optional: a host that doesn't aim can omit it). */
  hover?: Vec2 | null;
}

export interface ViewportHooks {
  /** This client's player id (null before the first observation). */
  meId(): number | null;
  /** What a left click at (x, y) means, from the observation. Pure. */
  primaryAction(obs: Observation, me: ObservedPlayer, x: number, y: number): PrimaryAction;
  /** Send the intent the primary action implies. */
  onPrimaryAction(action: PrimaryAction, sx?: number, sy?: number): void;
  /** Optional host-specific rendered-body resolver. The default resolves
   * live enemy bodies; the hub supplies the same projection for players. */
  pointTile?(obs: Observation, me: ObservedPlayer, point: ScreenPoint, positions: ReadonlyMap<string, Vec2>): Vec2 | null;
  /** Optional point-aware primary action. This preserves the identity of the
   * painter-frontmost body when two enemies share a tile; a tile-only action
   * cannot distinguish those targets after snapping. Return null to use the
   * normal tile resolver. */
  pointAction?(obs: Observation, me: ObservedPlayer, point: ScreenPoint, positions: ReadonlyMap<string, Vec2>): PrimaryAction | null;
  /** Optional point-aware menu. Used when several rendered bodies overlap so
   * right-click can list all of them instead of inheriting one tile winner. */
  contextItemsAtPoint?(obs: Observation, me: ObservedPlayer, point: ScreenPoint, positions: ReadonlyMap<string, Vec2>): CtxMenu | null;
  /** The right-click menu for a tile, or null to open nothing. */
  contextItems(obs: Observation, me: ObservedPlayer, x: number, y: number): CtxMenu | null;
  /** Display name for a player id (the game: lobby name · the arena: "You"). */
  nameFor(id: number): string;
  /** The party look for a player id (the game: the account's saved look,
   *  canon+LUT · absent/null = the historical identity tint). */
  lookFor?(id: number): PlayerLook | null;
  /** Host-only canvas pass, after the shared render (the arena's combat chrome). */
  overlay?(o: OverlayApi): void;
  /** Host keyboard (the arena's Space=attack / WASD). */
  onKey?(ev: KeyboardEvent): void;
}

export interface ScreenPoint {
  sx: number;
  sy: number;
  tile: Vec2;
  isoUnit: number;
  camX: number;
  camY: number;
}

export class Viewport {
  private canvas: HTMLCanvasElement;
  private hooks: ViewportHooks;
  private renderer: Renderer;
  private ctxMenu: ContextMenu;
  private readout: HTMLDivElement;
  private interp: InterpState;
  /** Cosmetic effect state (the projectile layer — packages/client
   *  effects.ts). Derived from the authoritative obs + tick events; the
   *  wall-clock is cosmetics-only (AGENTS §6). */
  private effects: EffectState;
  private rafId = 0;
  private lastFrameNow = 0;
  /** The previous frame's lerped positions (the sprite hit-test's "where is
   *  it drawn" source — set every frame, cleared on reset). Empty before the
   *  first frame; the hit-test falls back to authoritative tiles then. */
  private lastPositions = new Map<string, Vec2>();

  private obs: Observation | null = null;
  private prevObs: Observation | null = null;
  /** Wall clock the current observation was received (a step anchors here,
   *  not on the rAF frame that first renders it — see stepInterpolation's
   *  `stepAgeMs`). */
  private obsAt = 0;
  private tick = 0;
  /** This obs tick's wire events (the effect layer's hit/miss source),
   *  consumed once per tick by the frame loop. */
  private tickEvents: unknown[] | undefined;
  private hover: Vec2 | null = null;
  /** Transient click-feedback markers (spawned on pointer-down, pruned by
   *  expiry). Never cleared by server events — a marker acknowledges input,
   *  not success (R1). */
  private markers: Marker[] = [];
  private cursorMark: CursorMark | null = null;
  private keyHandler = (ev: KeyboardEvent): void => {
    this.hooks.onKey?.(ev);
  };
  private resizeHandler = (): void => {
    this.renderer.resize();
  };
  private resizeObserver: ResizeObserver;

  private debug: ViewportDebug | null;

  constructor(canvas: HTMLCanvasElement, root: HTMLElement, hooks: ViewportHooks, debug?: ViewportDebug) {
    this.canvas = canvas;
    this.hooks = hooks;
    this.interp = createInterpState();
    this.effects = createEffectState();
    this.debug = debug ?? null;
    this.renderer = new Renderer(canvas);
    this.ctxMenu = new ContextMenu(root);
    this.readout = document.createElement("div");
    this.readout.id = "hover-readout";
    root.appendChild(this.readout);
    this.wireInput();
    window.addEventListener("resize", this.resizeHandler);
    this.resizeObserver = new ResizeObserver(this.resizeHandler);
    this.resizeObserver.observe(canvas);
    window.addEventListener("keydown", this.keyHandler);
    window.addEventListener("keyup", this.keyHandler);
    this.renderer.resize();
    this.rafId = requestAnimationFrame(this.frame);
  }

  /** Camera state for the host's `__gameCam` dev probe. */
  get camera(): { camX: number; camY: number; isoUnit: number } {
    return { camX: this.renderer.camX, camY: this.renderer.camY, isoUnit: this.renderer.isoUnit };
  }

  /** The host's sprite atlas — set it once the async load resolves (players
   *  draw nothing until then, exactly like the game; null on a load failure).
   */
  setHeroAssets(assets: HeroAssets | null): void {
    this.renderer.heroAssets = assets;
  }

  /** Rendering-only room settings resolved from the local catalog. */
  setFloorLook(palette: string, architecture = "civic-stone-v1", wear = 24): void {
    this.renderer.floorPalette = palette;
    this.renderer.architecture = architecture;
    this.renderer.wear = wear;
  }

  /** Compatibility seam for hosts that only resolve a floor shade. */
  setFloorPalette(palette: string): void {
    this.setFloorLook(palette);
  }

  /** Host world props (a town's dungeon door, a plaza's props): static art
   *  the Renderer depth-sorts into the same painter pass as the heroes, so a
   *  hero walking in front of it draws over it and one walking behind draws
   *  under it. Set once — props are static; a prop that moves belongs in the
   *  observation. (The `overlay` hook cannot do this: it draws after every
   *  entity.) */
  setWorldProps(props: WorldProp[]): void {
    this.renderer.worldProps = props;
  }

  get heroAssets(): HeroAssets | null {
    return this.renderer.heroAssets;
  }

  /** Ingest an authoritative observation (+ this tick's wire events — the
   *  effect layer's hit/miss source; hosts that hold a synthetic obs pass
   *  none). The host then does its own post-processing (events, HUD,
   *  pending approaches) on the same obs. */
  ingest(obs: Observation, tick: number, events?: unknown[]): void {
    ingestEnemyDefs(obs);
    this.prevObs = this.obs;
    this.obs = obs;
    this.obsAt = performance.now();
    this.renderer.ingestPuzzles(obs.puzzles ?? [], this.obsAt);
    this.tick = tick;
    this.tickEvents = events;
    ingestEnemySpeech(this.enemyBubbles, this.speechTicks, obs, tick, events ?? [], this.obsAt);
  }

  private readonly enemyBubbles: ChatBubbleStore = new Map();
  private readonly speechTicks = new Map<number, number>();

  get observation(): Observation | null {
    return this.obs;
  }

  /** Reset the render state (the arena's respawn — clears the observation +
   *  the interpolation + transient chrome so a respawn snaps into place
   *  rather than walking across the map from the old position). */
  /** Draw ONE frame right now, without waiting for rAF. Headless probe
   *  tabs throttle requestAnimationFrame to near zero (the
   *  browser-debugging gotcha), so a synthetic obs pushed through ingest
   *  never paints on its own — the probe ingests, calls this, screenshots.
   *  Rendering-only: the interpolation clock is cosmetic. */
  redrawNow(): void {
    this.frame(performance.now());
  }

  reset(): void {
    this.renderer.clearPuzzleAnimations();
    this.obs = null;
    this.prevObs = null;
    this.obsAt = 0;
    this.tick = 0;
    this.tickEvents = undefined;
    this.interp = createInterpState();
    this.effects = createEffectState();
    this.enemyBubbles.clear();
    this.speechTicks.clear();
    this.lastPositions = new Map<string, Vec2>();
    this.markers = [];
    this.cursorMark = null;
    this.hover = null;
  }

  /** Open the right-click context menu (also used by the host's HUD item menu).
   *  `plate` is an optional non-interactive readout under the title — see
   *  `ContextMenu.open` and the rating plate (keyracing.md D1b). */
  openMenu(x: number, y: number, title: string, items: CtxItem[], plate?: HTMLElement): void {
    this.ctxMenu.open(x, y, title, items, plate);
  }

  /** Close any open context menu before a host-owned overlay takes focus. */
  closeMenu(): void {
    this.ctxMenu.close();
  }

  /** A menu-initiated action mirrors its left-click feedback (the menu is DOM,
   *  so the click never reaches the canvas — an interact verb flashes the
   *  target, "walk here" ripples the destination). */
  pushMenuMarker(obj: WorldObject | null, dest?: Vec2): void {
    const m = obj
      ? { kind: "interact" as const, obj, born: performance.now() }
      : dest
        ? { kind: "ground" as const, x: dest.x, y: dest.y, born: performance.now() }
        : null;
    if (m) this.markers = pushMarker(this.markers, m);
  }

  destroy(): void {
    cancelAnimationFrame(this.rafId);
    window.removeEventListener("resize", this.resizeHandler);
    this.resizeObserver.disconnect();
    window.removeEventListener("keydown", this.keyHandler);
    window.removeEventListener("keyup", this.keyHandler);
    this.readout.remove();
  }

  private screenPoint(ev: { clientX: number; clientY: number }): ScreenPoint {
    const rect = this.canvas.getBoundingClientRect();
    // Scale the mouse delta from the displayed canvas into the Renderer's
    // measured viewport space. They normally match; the ratio keeps input
    // correct through fractional CSS sizing and device-pixel rounding.
    const sx = (ev.clientX - rect.left) * (this.renderer.viewportW / rect.width);
    const sy = (ev.clientY - rect.top) * (this.renderer.viewportH / rect.height);
    return {
      sx,
      sy,
      tile: isoScreenToTile(sx, sy, this.renderer.isoUnit, this.renderer.camX, this.renderer.camY),
      isoUnit: this.renderer.isoUnit,
      camX: this.renderer.camX,
      camY: this.renderer.camY,
    };
  }

  /** The click/hover-resolved tile: a point on a live enemy's rendered sprite
   *  snaps to its foot tile. Hosts with pointAction resolve primary clicks
   *  before this projection so sprite and bare ground can have different
   *  actions even when they map to the same tile. */
  private clickTile(ev: { clientX: number; clientY: number }): Vec2 {
    const pt = this.screenPoint(ev);
    if (!this.obs) return pt.tile;
    const meId = this.hooks.meId();
    const me = meId === null ? null : this.obs.players.find((p) => p.id === meId) ?? null;
    const hostTile = me ? this.hooks.pointTile?.(this.obs, me, pt, this.lastPositions) : null;
    if (hostTile) return hostTile;
    const e = enemyAtScreenPoint(
      this.heroAssets,
      this.obs,
      this.lastPositions,
      pt.sx,
      pt.sy,
      this.renderer.isoUnit,
      this.renderer.camX,
      this.renderer.camY,
    );
    return e ? { x: e.x, y: e.y } : pt.tile;
  }

  private wireInput(): void {
    // The world surface is a pointer target, never a draggable/selectable
    // document image. This prevents fast right-clicks from selecting the
    // canvas or starting a native drag while leaving the HUD's intentional
    // inventory reorder handlers untouched.
    this.canvas.draggable = false;
    this.canvas.style.userSelect = "none";
    this.canvas.style.webkitUserSelect = "none";
    this.canvas.style.touchAction = "none";
    const suppressNative = (ev: Event): void => ev.preventDefault();
    const clearSelection = (): void => {
      window.getSelection()?.removeAllRanges();
    };
    this.canvas.addEventListener("dragstart", suppressNative);
    this.canvas.addEventListener("selectstart", suppressNative);
    this.canvas.addEventListener("auxclick", suppressNative);
    this.canvas.addEventListener("mousemove", (ev) => {
      this.hover = this.clickTile(ev);
      this.updateReadout(this.hover, ev);
    });
    this.canvas.addEventListener("mouseleave", () => {
      this.hover = null;
      this.readout.textContent = "";
    });
    // Primary action fires on pointer-down (left button), not click — the
    // marker must spawn in the same frame as the press, before any
    // pathfinding/network (R1).
    this.canvas.addEventListener("pointerdown", (ev) => {
      // A right/auxiliary press must never enter the browser's selection or
      // drag machinery. The later contextmenu/auxclick events are too late:
      // Chrome can already have painted a text selection by then.
      if (ev.button !== 0) {
        ev.preventDefault();
        clearSelection();
        return;
      }
      ev.preventDefault();
      clearSelection();
      const meId = this.hooks.meId();
      if (!this.obs || meId === null) return;
      const me = this.obs.players.find((p) => p.id === meId);
      if (!me) return;
      const point = this.screenPoint(ev);
      const pointAction = this.hooks.pointAction?.(this.obs, me, point, this.lastPositions);
      const tile = pointAction ? null : this.clickTile(ev);
      const action = pointAction ?? this.hooks.primaryAction(this.obs, me, tile!.x, tile!.y);
      const markerTile = pointAction && pointAction.kind === "attack"
        ? { x: pointAction.x, y: pointAction.y }
        : pointAction && pointAction.kind === "interact"
          ? { x: pointAction.obj.x, y: pointAction.obj.y }
          : pointAction && pointAction.kind === "enter"
            ? { x: pointAction.door.x, y: pointAction.door.y }
            : pointAction && pointAction.kind === "openSack"
              ? { x: pointAction.x, y: pointAction.y }
              : pointAction && pointAction.kind === "walk"
                ? { x: pointAction.x, y: pointAction.y }
                : tile ?? point.tile;
      const born = performance.now();
      this.markers = pushMarker(this.markers, markerForAction(action, markerTile.x, markerTile.y, born));
      this.cursorMark = cursorMarkForAction(action, point.sx, point.sy, born);
      this.hooks.onPrimaryAction(action, ev.clientX, ev.clientY);
    });
    this.canvas.addEventListener("contextmenu", (ev) => {
      ev.preventDefault();
      clearSelection();
      const point = this.screenPoint(ev);
      const meId = this.hooks.meId();
      if (!this.obs || meId === null) return;
      const me = this.obs.players.find((p) => p.id === meId);
      if (!me) return;
      const pointItems = this.hooks.contextItemsAtPoint?.(this.obs, me, point, this.lastPositions);
      if (pointItems) {
        this.ctxMenu.open(ev.clientX, ev.clientY, pointItems.title, pointItems.items, pointItems.plate);
        return;
      }
      const tile = this.clickTile(ev);
      const items = this.hooks.contextItems(this.obs, me, tile.x, tile.y);
      if (items) this.ctxMenu.open(ev.clientX, ev.clientY, items.title, items.items, items.plate);
    });
  }

  private updateReadout(tile: Vec2, ev?: { clientX: number; clientY: number }): void {
    const meId = this.hooks.meId();
    if (!this.obs || meId === null) {
      this.readout.textContent = "";
      return;
    }
    const me = this.obs.players.find((p) => p.id === meId);
    if (!me) {
      this.readout.textContent = "";
      return;
    }
    const pointAction = ev
      ? this.hooks.pointAction?.(this.obs, me, this.screenPoint(ev), this.lastPositions)
      : null;
    const action = pointAction ?? this.hooks.primaryAction(this.obs, me, tile.x, tile.y);
    // Name the action (walk/interact/enter/attack carry a label; self/none don't).
    this.readout.textContent = "label" in action ? action.label : "";
  }

  private entitiesFromObs(obs: Observation): MovingEntity[] {
    const out: MovingEntity[] = [];
    // The step duration comes from the mover's AUTHORITATIVE cadence: the
    // run's hero pace (obs.combat.moveCostTicks — the sim's own table) for
    // players, the enemy's effective moveSpeedTicks (the sim resolved the
    // fallback server-side) for foes — so a slow dragon glides the whole
    // 2500 ms of its step instead of covering the tile early and
    // idle-stalling until the next one. A SPRINTING hero interpolates over
    // the buff's resolved cadence (speedBuff.costTicks — the sim resolved it
    // there, one implementation), so a buffed hero never lags its tile.
    for (const p of obs.players) {
      const ticks = p.speedBuff?.costTicks ?? obs.combat.moveCostTicks;
      out.push({ id: playerKey(p.id), x: p.x, y: p.y, stepMs: ticks * TICK_MS });
    }
    for (const e of obs.enemies) {
      const ticks = e.moveSpeedTicks ?? obs.combat.moveCostTicks;
      // Enemies are the only movers an ability may FORCE-MOVE across several
      // tiles in one step (a Black Hole pull) — their multi-tile delta
      // renders as a short "sucked in" slide (the interp's draggable drag),
      // not a teleport.
      out.push({ id: enemyKey(e.id), x: e.x, y: e.y, stepMs: ticks * TICK_MS, draggable: true, teleportTick: e.blinkedAt });
    }
    return out;
  }

  private frame = (now: number): void => {
    if (this.obs) {
      const dtMs = Math.min(100, Math.max(0, now - this.lastFrameNow));
      this.lastFrameNow = now;
      const entities = this.entitiesFromObs(this.obs);
      const prevEntities = this.prevObs ? this.entitiesFromObs(this.prevObs) : null;
      const { positions, anims } = stepInterpolation(
        this.interp,
        entities,
        prevEntities,
        now,
        dtMs,
        Math.max(0, now - this.obsAt),
      );
      this.lastPositions = positions;
      // Overlay the authoritative swing (attack frame + facing) onto the
      // walk/idle anims — the arena's combat; a no-op for the dungeon (no
      // enemies, no hero attack).
      applyCombatAnims(anims, this.obs, this.tick);
      applyPounceVisuals(this.interp, positions, this.obs, this.tick,
        Math.min(1, Math.max(0, now-this.obsAt)/TICK_MS));
      // Advance the cosmetic effect layer (projectile missiles + impact
      // bursts/fizzles — pure over obs + tick + the events of this tick).
      const effectFrame = stepEffects(this.effects, this.obs, this.tick, this.tickEvents, positions, now);
      // Prune expired markers (they acknowledge input for ≤400 ms, then vanish).
      this.markers = this.markers.filter((m) => !markerExpired(m, now, MARKER_CONFIG));
      if (this.cursorMark && now - this.cursorMark.born >= CURSOR_MARK_DURATION) this.cursorMark = null;
      if (this.debug) {
        this.debug.positions = positions;
        this.debug.anims = anims;
        this.debug.markers = this.markers;
        this.debug.cursorMark = this.cursorMark;
        this.debug.hover = this.hover;
      }
      const meId = this.hooks.meId();
      if (meId !== null) {
        const st: RenderState = { positions, anims, hover: this.hover, markers: this.markers, effects: effectFrame };
        this.renderer.render(this.obs, meId, st, now, (id) => this.hooks.nameFor(id), this.hooks.lookFor, this.tick);
        // Effects draw AFTER the painter pass (a missile flies over the
        // world, a hitsplat reads over its combatant), in the renderer's
        // camera-translated space.
        if (
          effectFrame.missiles.length > 0 ||
          effectFrame.bursts.length > 0 ||
          effectFrame.splats.length > 0 ||
          effectFrame.grabs.length > 0
        ) {
          const c = this.rendererCtx();
          c.save();
          c.translate(-this.renderer.camX, -this.renderer.camY);
          drawEffects(c, effectFrame, this.renderer.isoUnit, now);
          c.restore();
        }
        if (this.hooks.overlay) {
          this.hooks.overlay({
            ctx: this.rendererCtx(),
            obs: this.obs,
            meId,
            positions,
            anims,
            camX: this.renderer.camX,
            camY: this.renderer.camY,
            isoUnit: this.renderer.isoUnit,
            cssW: this.renderer.viewportW,
            cssH: this.renderer.viewportH,
            now,
          });
        }
        drawChatBubbles({ ctx: this.rendererCtx(), positions, camX: this.renderer.camX, camY: this.renderer.camY,
          isoUnit: this.renderer.isoUnit, cssW: this.renderer.viewportW, cssH: this.renderer.viewportH, now },
          this.enemyBubbles, (id) => {
            const e = this.obs!.enemies.find((enemy) => enemy.id === id);
            const family = e ? enemyByKind(e.kind)?.family ?? e.kind : "";
            return this.heroAssets?.atlas.npcs?.[family]?.anchor[1] ?? 96;
          }, true, enemyKey);
        if (this.cursorMark) drawCursorMark(this.rendererCtx(), this.cursorMark, now);
      }
    }
    this.rafId = requestAnimationFrame(this.frame);
  };

  private rendererCtx(): CanvasRenderingContext2D {
    // The Renderer owns the 2d context; getContext is idempotent (returns the
    // same context, still in CSS-pixel space after its render() save/restore).
    return this.canvas.getContext("2d")!;
  }
}
